import { Suspense, useMemo, useState } from "react";
import { IMessagesTemplate } from "@/types/messages-template.type";
import GenericTable from "@/common/components/ui/table/GenericTable";
import Loading from "@/common/components/ui/loading/Loading";
import {
  TableFilters,
} from "../../../../common/components/ui/table/TableFilters";
import { FormMessagesTemplate } from "../components/modals/FormMessagesTemplate";
import { useDeleteMessageTemplate, useMarketingMessageTemplates, useUpdateMessageTemplate } from "@/common/hooks";
import TableCursorFooter from "@/common/components/ui/table/TableCursorFooter";
import { Modal } from "flowbite-react";
import ButtonWithLoader from "@/common/components/ui/loading/ButtonLoading";

const headers = {
  name: "Nome",
  message: "Mensagem",
  status: "Status",
};

export default function MessagesTemplateTable() {
  const [action, setAction] = useState<{ eventName: string; data?: unknown }>({ eventName: "", data: undefined });
  const { data: messages, fetchNextPage, hasNextPage, isFetchingNextPage, refetch } = useMarketingMessageTemplates();
  const { mutateAsync: deleteTemplate, isPending: isPendingDelete } = useDeleteMessageTemplate();
  const { mutateAsync: updateTemplateMessage } = useUpdateMessageTemplate();

  const flattenedData = useMemo(() => messages?.pages.flatMap(page => page?.data || []) ?? [], [messages]);

  const handleSave = () => {
    const data = action.data as IMessagesTemplate; 

    updateTemplateMessage({
      id: data?.id,
      name: data?.name,
      message: data?.components?.find(c => c.type === "BODY")?.text
    });
    handleClose();
    refetch();
  };

  const handleClose = () => {
    setAction({ eventName: "", data: undefined });
  };

  const actions = [
    {
      label: "Editar",
      action: (data: unknown) =>
        setAction({ eventName: "edit", data: data as IMessagesTemplate }),
    },
    {
      label: "Deletar",
      action: (data: unknown) =>
        setAction({ eventName: "delete", data: data as IMessagesTemplate }),
    },
  ];

  const handleFilterChange = () => {
    refetch();
  }

  const handleDeleteConfirm = () => {
    const { data } =  action;
    const { name } = data as IMessagesTemplate;

    deleteTemplate(name);
    handleClose();
    refetch();
  }

  return (
    <Suspense fallback={<Loading />}>
      <div className="table-section">
        <TableFilters
          statusOptions={{}}
          dashboardHeader="Templates de Mensagens"
          placeholder="Digite o nome do template"
          newItemLabel="Novo Template"
          onNewItemClick={() =>
            setAction({ eventName: "create", data: undefined })
          }
          onSearch={handleFilterChange}
          entity="vehicles"
          tableData={flattenedData as unknown as Record<string, unknown>[]}
        />
        <GenericTable
          headers={headers}
          data={flattenedData}
          actions={actions}
          entity="vehicles"
          renders={{
            message: (_, data) => {
              return (
                <span className="line-clamp-4">{(data as IMessagesTemplate)?.components?.find((c) => c.type === "BODY")?.text}</span>
              )
            },
            status: (status: unknown) => {
              return (
                <span
                  className={`inline-block px-2 py-1 rounded-md text-sm whitespace-nowrap overflow-hidden text-ellipsis max-w-[120px] md:max-w-full ${
                    status ? "bg-green-500 text-white" : "bg-red-500 text-white"
                  }`}
                >
                  {status ? "Ativo" : "Inativo"}
                </span>
              );
            },
          }}
        />
        <TableCursorFooter
          onLoadMore={fetchNextPage}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
        />
      </div>
      {(action.eventName === "create" || action.eventName === "edit") && (
        <FormMessagesTemplate
          isOpen={action.eventName === "create" || action.eventName === "edit"}
          onClose={handleClose}
          onSave={handleSave}
          initialData={action.data as IMessagesTemplate}
        />
      )}

       {action.eventName === "delete" && (
         <Modal show={action.eventName === "delete"} onClose={handleClose}>
           <Modal.Header>Confirmar Exclusão</Modal.Header>
           <Modal.Body>
             <p>Tem certeza que deseja excluir o template <b>{(action?.data as IMessagesTemplate)?.name}</b>?</p>
           </Modal.Body>
           <Modal.Footer>
             <ButtonWithLoader
               onClick={handleDeleteConfirm}
               isLoading={isPendingDelete}
             >
               Excluir
             </ButtonWithLoader>
             <ButtonWithLoader onClick={handleClose}>
               Cancelar
             </ButtonWithLoader>
           </Modal.Footer>
         </Modal>
       )}
    </Suspense>
  );
}
